/**
 * Clubbing of Income (Sections 60-64) — AXIORA (Feb 2026)
 *
 * Route: /tax/clubbing
 * Sections supported: 60, 61, 64(1)(ii), 64(1)(iv), 64(1)(vi), 64(1A) minor
 *                     child (₹1,500 exemption), 64(2) HUF, other.
 * The engine sums by destination head → pushes into the ITR computation's
 * `clubbing_by_head` dict. Recompute merges into each head's `auto`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Send, X, Check, Users, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const SECTIONS = [
  { value: "60",            label: "Sec 60 — Transfer of income without transfer of asset" },
  { value: "61",            label: "Sec 61 — Revocable transfer of assets" },
  { value: "64_1_ii",       label: "Sec 64(1)(ii) — Spouse's salary from concern with substantial interest" },
  { value: "64_1_iv",       label: "Sec 64(1)(iv) — Income from asset transferred to spouse" },
  { value: "64_1_vi",       label: "Sec 64(1)(vi) — Income from asset transferred to son's wife" },
  { value: "64_1a_minor",   label: "Sec 64(1A) — Minor child's income (₹1,500 exemption available)" },
  { value: "64_2_huf",      label: "Sec 64(2) — HUF property (from converted individual property)" },
  { value: "other",         label: "Other clubbing" },
];
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.value, s.label]));
const SECTION_SHORT = {
  "60": "Sec 60", "61": "Sec 61", "64_1_ii": "Sec 64(1)(ii)",
  "64_1_iv": "Sec 64(1)(iv)", "64_1_vi": "Sec 64(1)(vi)",
  "64_1a_minor": "Sec 64(1A)", "64_2_huf": "Sec 64(2)", "other": "Other",
};

const HEADS = [
  { value: "salary",              label: "Salary" },
  { value: "house_property",      label: "House Property" },
  { value: "business_profession", label: "Business / Profession" },
  { value: "capital_gains",       label: "Capital Gains" },
  { value: "other_sources",       label: "Other Sources" },
];
const HEAD_LABEL = Object.fromEntries(HEADS.map((h) => [h.value, h.label]));

const currentAY = () => {
  const d = new Date();
  const fyStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${fyStart + 1}-${String((fyStart + 2) % 100).padStart(2, "0")}`;
};

const EMPTY_ROW = () => ({
  section: "64_1a_minor",
  related_person: "",
  destination_head: "other_sources",
  gross_amount: 0,
  apply_minor_exemption: true,
  include_in_computation: true,
  notes: "",
});

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ay, setAy] = useState(currentAY());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pushOpen, setPushOpen] = useState(false);
  const [computations, setComputations] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState("");

  const refresh = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await api.get(`/companies/${activeId}/clubbing/summary`, { params: { ay } });
      setRows(r.data.rows || []);
      setSummary(r.data);
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [activeId, ay]);

  const openNew  = () => { setEditing({ ...EMPTY_ROW() }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setDialogOpen(true); };
  const save = async () => {
    try {
      const payload = { ...editing, ay };
      if (editing.id) {
        await api.patch(`/companies/${activeId}/clubbing/rows/${editing.id}`, payload);
      } else {
        await api.post(`/companies/${activeId}/clubbing/rows`, payload);
      }
      toast.success("Saved");
      setDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete this clubbing row?`)) return;
    try {
      await api.delete(`/companies/${activeId}/clubbing/rows/${r.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };
  const openPushDialog = async () => {
    setPushOpen(true);
    try {
      const r = await api.get(`/companies/${activeId}/tax/computations`);
      setComputations(r.data || []);
      setSelectedCompId(r.data?.[0]?.id || "");
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };
  const pushToITR = async () => {
    if (!selectedCompId) return;
    try {
      const r = await api.post(
        `/companies/${activeId}/clubbing/push-to-itr/${selectedCompId}`,
        null, { params: { ay } },
      );
      const heads = r.data.patch.clubbing_by_head || {};
      const summary = Object.entries(heads).map(([h, v]) => `${HEAD_LABEL[h]}: ₹${formatINR(v)}`).join(", ");
      toast.success(`Pushed to ITR — ${summary || "no included rows"}`);
      setPushOpen(false);
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const totals = summary?.totals || { gross: 0, exemption: 0, net: 0, included_net: 0, total_rows: 0 };
  const perHead = summary?.per_head || {};

  return (
    <>
      <PageHeader
        title="Clubbing of Income · Sections 60-64"
        subtitle="Log each clubbed item once. The engine adds them to the right head and applies Sec 10(32) exemption for minors."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/tax")} className="h-8" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <select
              value={ay}
              onChange={(e) => setAy(e.target.value)}
              className="h-8 px-2 border border-gray-300 rounded text-xs"
              data-testid="select-ay"
            >
              {["2024-25", "2025-26", "2026-27", "2027-28"].map((a) => <option key={a} value={a}>AY {a}</option>)}
            </select>
            <Button
              onClick={openPushDialog}
              variant="secondary"
              className="h-8"
              disabled={!totals.included_net}
              data-testid="btn-push-itr"
            >
              <Send className="w-4 h-4 mr-1" /> Push to ITR
            </Button>
            <Button onClick={openNew} className="h-8 bg-fuchsia-700 hover:bg-fuchsia-800" data-testid="btn-new-row">
              <Plus className="w-4 h-4 mr-1" /> Add row
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="clubbing-kpis">
          <Kpi label="Gross Clubbed" value={totals.gross} tone="gray" testid="kpi-gross" />
          <Kpi label="Sec 10(32) Exemption" value={totals.exemption} tone="rose" testid="kpi-exemption" />
          <Kpi label="Net Clubbed" value={totals.net} tone="emerald" testid="kpi-net" />
          <Kpi label="Included in ITR" value={totals.included_net} tone="blue" testid="kpi-included" />
        </div>

        {/* Per-head split */}
        {totals.total_rows > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3" data-testid="per-head-cards">
            {HEADS.map((h) => (
              <div key={h.value} className="border border-gray-200 bg-white rounded p-3" data-testid={`per-head-${h.value}`}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">{h.label}</div>
                <div className="text-sm font-medium text-gray-900 mt-0.5">₹ {formatINR(perHead[h.value] || 0)}</div>
              </div>
            ))}
          </div>
        )}

        <div className="border border-fuchsia-200 bg-fuchsia-50 rounded p-3 text-xs text-fuchsia-900 flex gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Each row is added to the destination head of your own computation.
            For <strong>Sec 64(1A) minor child</strong>, toggle the exemption to auto-deduct
            ₹1,500 per minor. Uncheck <em>Include</em> on any row to exclude it from the push.
          </div>
        </div>

        {/* Rows */}
        <div className="border border-gray-200 bg-white rounded overflow-hidden" data-testid="rows-table">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-700 flex items-center justify-between">
            <span>Clubbed items — AY {ay}</span>
            {loading && <span className="text-gray-400">Loading…</span>}
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
              <Users className="w-10 h-10 text-gray-300" />
              No clubbed items recorded for AY {ay}.
            </div>
          ) : (
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Section</th>
                  <th>Related person</th>
                  <th>Destination head</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Exemption</th>
                  <th className="text-right">Net</th>
                  <th>Included?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`row-${i}`}>
                    <td className="text-xs">{SECTION_SHORT[r.section] || r.section}</td>
                    <td className="text-xs">{r.related_person || "—"}</td>
                    <td className="text-xs">{HEAD_LABEL[r.destination_head] || r.destination_head}</td>
                    <td className="tx-num">{formatINR(r.gross_amount)}</td>
                    <td className="tx-num">{formatINR(r.exemption_applied)}</td>
                    <td className="tx-num font-medium">{formatINR(r.net_clubbed)}</td>
                    <td className="text-xs">{r.include_in_computation ? "✓" : "—"}</td>
                    <td className="whitespace-nowrap text-right">
                      <button onClick={() => openEdit(r)} className="text-blue-700 hover:underline mr-2" title="Edit" data-testid={`btn-edit-${i}`}>
                        <Pencil className="w-3.5 h-3.5 inline" />
                      </button>
                      <button onClick={() => del(r)} className="text-red-600 hover:text-red-800" title="Delete" data-testid={`btn-delete-${i}`}>
                        <Trash2 className="w-3.5 h-3.5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="row-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Add"} Clubbing Row</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <FieldSelect label="Section" value={editing.section} onChange={(v) => setEditing({ ...editing, section: v })} testid="select-section">
                {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </FieldSelect>
              <Field label="Related person" placeholder='e.g. "Minor: Rohan (age 8)"' value={editing.related_person} onChange={(v) => setEditing({ ...editing, related_person: v })} testid="input-related" />
              <FieldSelect label="Destination head (adds into assessee's return)" value={editing.destination_head} onChange={(v) => setEditing({ ...editing, destination_head: v })} testid="select-head">
                {HEADS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </FieldSelect>
              <FieldNum label="Gross amount (₹)" value={editing.gross_amount} onChange={(v) => setEditing({ ...editing, gross_amount: v })} testid="input-gross" />
              {editing.section === "64_1a_minor" && (
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={editing.apply_minor_exemption}
                    onChange={(e) => setEditing({ ...editing, apply_minor_exemption: e.target.checked })}
                    data-testid="check-minor-exemption"
                  />
                  Apply Sec 10(32) ₹1,500 exemption for this minor
                </label>
              )}
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={editing.include_in_computation}
                  onChange={(e) => setEditing({ ...editing, include_in_computation: e.target.checked })}
                  data-testid="check-include"
                />
                Include in ITR push
              </label>
              <Field label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} testid="input-notes" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} className="bg-fuchsia-700 hover:bg-fuchsia-800" data-testid="btn-save"><Check className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push to ITR dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid="push-dialog">
          <DialogHeader>
            <DialogTitle>Push clubbing to ITR</DialogTitle>
          </DialogHeader>
          {computations.length === 0 ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              No saved computations found. <a href="/tax/computation/new" className="underline">Create one first</a> for AY {ay}.
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Choose the target computation</Label>
              <select
                value={selectedCompId}
                onChange={(e) => setSelectedCompId(e.target.value)}
                className="w-full h-9 px-2 border border-gray-300 rounded text-xs"
                data-testid="select-target-comp"
              >
                {computations.map((c) => (
                  <option key={c.id} value={c.id}>{c.ay} — {c.profile_snapshot?.name || c.profile_snapshot?.taxpayer_type}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500">
                Pushing per-head totals of INCLUDED rows to the computation&apos;s <code>clubbing_by_head</code> field.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushOpen(false)}>Cancel</Button>
            <Button
              onClick={pushToITR}
              disabled={!selectedCompId}
              className="bg-fuchsia-700 hover:bg-fuchsia-800"
              data-testid="btn-confirm-push"
            >
              <Send className="w-4 h-4 mr-1" /> Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Small presentational helpers */
function Kpi({ label, value, tone = "gray", testid }) {
  const bg = { gray: "bg-gray-50", rose: "bg-rose-50", emerald: "bg-emerald-50", blue: "bg-blue-50" }[tone];
  const fg = { gray: "text-gray-800", rose: "text-rose-800", emerald: "text-emerald-800", blue: "text-blue-800" }[tone];
  return (
    <div className={`border border-gray-200 ${bg} rounded p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
      <div className={`text-lg font-medium ${fg}`}>₹ {formatINR(value)}</div>
    </div>
  );
}
function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" data-testid={testid} placeholder={placeholder} />
    </div>
  );
}
function FieldNum({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input type="number" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, children, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid={testid}>
        {children}
      </select>
    </div>
  );
}

export default function Clubbing() {
  return <RequireCompany><Inner /></RequireCompany>;
}
